import { convertFhirToCcda, convertCcdaToXml } from '@medplum/ccda'
import { Bundle, Composition, Patient } from '@medplum/fhirtypes'
import { createReference, ProfileResource } from '@medplum/core'
import { formatDateTime } from '@/utils'
import type { CareCoordinatorNoteFormValues } from '../../Patient/components/careCoordinatorSchema'
import {
  formatCareCoordinatorCallDuration,
  getEncounterTypeLabel,
  getOutcomeLabel,
  getReasonForCallLabel,
} from '../../Patient/components/careCoordinatorSchema'

function getNoteDate(noteDate?: string): Date {
  if (!noteDate) {
    return new Date()
  }
  const parsed = new Date(noteDate)
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed
}

const CODES = {
  NOTE: { code: '11488-4', display: 'Note' },
  ENCOUNTER: {
    code: '46240-8',
    display: 'History of hospitalizations and outpatient visits Narrative',
  },
  VITALS: { code: '8716-3', display: 'Vital signs' },
  ALLERGIES: { code: '48765-2', display: 'Allergies and adverse reactions Document' },
  MEDS: { code: '10160-0', display: 'History of Medication use Narrative' },
  PROBLEMS: { code: '11450-4', display: 'Problem list - Reported' },
  PROCEDURES: { code: '47519-4', display: 'History of Procedures Document' },
  SOCIAL: { code: '29762-2', display: 'Social history Narrative' },
  ASSESSMENT: { code: '51848-0', display: 'Assessments' },
  PLAN: { code: '18776-5', display: 'Plan of care note' },
  BILLING: { code: '42349-1', display: 'Billing information' },
} as const

const xhtml = (html: string) => `<div>${html}</div>`

const esc = (s?: string) =>
  (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const table = (headers: string[] | undefined, rows: string[][]) => {
  rows = rows.filter((items) => items.some((item) => !!item))
  if (!rows.length) return ''

  const header = `<tr>${headers?.map((h) => `<th>${esc(h)}</th>`).join('')}</tr>`

  const body = rows.map(
    (items) => `<tr>${items.map((item) => `<td>${esc(item)}</td>`).join('')}</tr>`,
  )

  return body.length
    ? `<table>${headers ? `<thead>${header}</thead>` : ''}<tbody>${body.join('\n')}</tbody></table>`
    : '<p>Not on file</p>'
}

export type CareCoordinatorCDAProps = {
  patient: Patient
  profile: ProfileResource
  values: CareCoordinatorNoteFormValues
  /** ISO or date-only string; defaults to now */
  noteDate?: string
  practitionerDisplay?: string
}

/**
 * C-CDA XML for Care Coordinator progress notes via {@link convertFhirToCcda} /
 * {@link convertCcdaToXml}. No Encounter on Composition.
 */
export function generateCareCoordinatorCDA({
  patient,
  profile,
  values,
  noteDate,
  practitionerDisplay,
}: CareCoordinatorCDAProps): string {
  const authoredDate = getNoteDate(noteDate)
  const authoredDateIso = authoredDate.toISOString()

  const composition: Composition = {
    resourceType: 'Composition',
    subject: createReference(patient),
    title: 'Progress Note (Care Coordinator)',
    status: 'final',
    author: [
      {
        ...createReference(profile),
        display: practitionerDisplay || createReference(profile).display,
      },
    ],
    type: {
      coding: [
        { system: 'http://loinc.org', code: '11506-3', display: 'Subsequent evaluation note' },
      ],
      text: 'Progress Note (Care Coordinator)',
    },
    date: authoredDateIso,
    section: [],
  }

  const encounterDetailsHtml = table(
    ['Field', 'Value'],
    [
      ['Date', formatDateTime(authoredDate.toISOString(), '/', true)],
      ['Encounter type', getEncounterTypeLabel(values.encounterType)],
      ['Reason for call', getReasonForCallLabel(values.reasonForCall)],
      ['Call duration', formatCareCoordinatorCallDuration(values.callDurationSeconds)],
    ],
  )

  const quickNoteHtml = values.quickNote
    .split('\n')
    .map((line) => `<p>${esc(line)}</p>`)
    .join('')

  const followUpRows: [string, string][] = [
    ['Outcome', getOutcomeLabel(values.outcome)],
    ['Follow-up needed', values.followUpNeeded === 'yes' ? 'Yes' : 'No'],
  ]
  if (values.followUpNeeded === 'yes' && values.followUpDate) {
    followUpRows.push(['Follow-up date', values.followUpDate])
  }
  const outcomeFollowUpHtml = table(['Field', 'Value'], followUpRows)

  composition.section = [
    {
      title: 'Encounter Details',
      code: { coding: [{ system: 'http://loinc.org', ...CODES.ENCOUNTER }] },
      text: { status: 'generated', div: xhtml(encounterDetailsHtml) },
    },
    {
      title: 'Quick note',
      code: { coding: [{ system: 'http://loinc.org', ...CODES.NOTE }] },
      text: { status: 'generated', div: xhtml(quickNoteHtml) },
    },
    {
      title: 'Outcome and follow-up',
      code: { coding: [{ system: 'http://loinc.org', ...CODES.PLAN }] },
      text: { status: 'generated', div: xhtml(outcomeFollowUpHtml) },
    },
  ]

  const bundle: Bundle = {
    resourceType: 'Bundle',
    type: 'collection',
    entry: [{ resource: patient }, { resource: composition }],
  }

  const cda = convertFhirToCcda(bundle, {
    type: 'summary',
  })

  return convertCcdaToXml(cda)
}
